import type { DemoScript } from '../../../types/demo';

const AUDIO_BASE = 'https://bigsplice-demo-audio.s3.amazonaws.com/call-center';
const AUDIO_VERSION = 'v5';

/** Hardcoded demo file representing a sample call recording */
const DEMO_FILE = {
  fileName: 'demo_call.mp3',
  fileSize: 4200000,
  uploadDate: '2026-02-23T10:00:00Z',
  s3Key: 'users/demo/demo_call.mp3',
  contentType: 'audio/mpeg',
};

/** Mock schema for call center transcription data */
const DEMO_SCHEMA = [
  { name: 'call_id', data_type: 'INT64' },
  { name: 'caller', data_type: 'BYTE_ARRAY' },
  { name: 'transcript', data_type: 'BYTE_ARRAY' },
  { name: 'duration', data_type: 'INT64' },
  { name: 'sentiment', data_type: 'BYTE_ARRAY' },
  { name: 'date', data_type: 'BYTE_ARRAY' },
  { name: 'agent', data_type: 'BYTE_ARRAY' },
  { name: 'category', data_type: 'BYTE_ARRAY' },
];

/** Mock rows of transcribed call center data */
const DEMO_ROWS = [
  { call_id: 1001, caller: 'Sarah Mitchell', transcript: 'Hi, I was calling about my recent order. I placed it three days ago and still no tracking number...', duration: 342, sentiment: 'neutral', date: '2026-02-21', agent: 'Mike R.', category: 'Order Inquiry' },
  { call_id: 1002, caller: 'James Wilson', transcript: 'I need to file a complaint about my delivery. The package arrived damaged and two items were missing...', duration: 518, sentiment: 'negative', date: '2026-02-21', agent: 'Lisa K.', category: 'Complaint' },
  { call_id: 1003, caller: 'Maria Lopez', transcript: "I'd like to request a refund for my subscription. I've been charged twice this month...", duration: 267, sentiment: 'negative', date: '2026-02-20', agent: 'Tom B.', category: 'Refund Request' },
  { call_id: 1004, caller: 'David Chen', transcript: "Thank you for resolving my issue last time. I'm calling to upgrade my plan to premium...", duration: 195, sentiment: 'positive', date: '2026-02-20', agent: 'Sarah P.', category: 'Upgrade' },
  { call_id: 1005, caller: 'Emily Johnson', transcript: "I've been on hold for twenty minutes. This is unacceptable customer service...", duration: 612, sentiment: 'negative', date: '2026-02-19', agent: 'Mike R.', category: 'Complaint' },
  { call_id: 1006, caller: 'Robert Taylor', transcript: 'Your team handled my return perfectly. The replacement arrived next day. Really impressed...', duration: 156, sentiment: 'positive', date: '2026-02-19', agent: 'Lisa K.', category: 'Positive Feedback' },
];

/** Mock search results for "customer complaint" query */
const DEMO_SEARCH_RESULTS = [
  { call_id: 1002, caller: 'James Wilson', transcript: 'I need to file a complaint about my delivery...', sentiment: 'negative', category: 'Complaint' },
  { call_id: 1005, caller: 'Emily Johnson', transcript: 'Customer complaint: been on hold for twenty minutes...', sentiment: 'negative', category: 'Complaint' },
  { call_id: 1008, caller: 'Karen Davis', transcript: 'Customer complaint regarding incorrect billing on my last statement...', sentiment: 'negative', category: 'Billing Dispute' },
];

/**
 * Call Center Analytics demo script with LIVE DATA injection.
 * Uses hardcoded demo_call.mp3 with mock call center transcript data
 * flowing through all 6 nodes of the call-center flow template.
 */
export const callCenterScript: DemoScript = {
  id: 'call-center',
  templateId: 'call-center',
  name: 'Call Center Analytics Demo',
  description: 'Learn how to transcribe, browse, and search call center recordings.',
  steps: [
    // === INTRO (no target) ===
    {
      id: 'intro',
      type: 'narrate',
      delayBefore: 300,
      subtitle: 'Hey there. Let me show you how this works \u2014 I\u2019ll walk you through a call center analytics pipeline, step by step.',
      audio: { src: `${AUDIO_BASE}/intro.mp3?${AUDIO_VERSION}`, duration: 6000 },
    },

    // === STEP 1: Upload Recording ===
    {
      id: 'upload-highlight',
      type: 'highlight',
      target: { nodeId: 'upload' },
      delayBefore: 200,
      subtitle: 'This is where your call recordings land. Drag and drop audio files \u2014 we support MP3, WAV, M4A, and more.',
      audio: { src: `${AUDIO_BASE}/upload-highlight.mp3?${AUDIO_VERSION}`, duration: 12000 },
      cursorPath: [
        { x: -80, y: -40, duration: 600 },
        { x: 0, y: 0, duration: 500 },
      ],
    },
    {
      id: 'upload-sample-note',
      type: 'narrate',
      target: { nodeId: 'upload' },
      subtitle: 'Quick heads up \u2014 this walkthrough uses simulated data so I can show you the full pipeline without any wait. When you\u2019re ready to try it for real, just upload your own recordings.',
      audio: { src: `${AUDIO_BASE}/upload-sample-note.mp3?${AUDIO_VERSION}`, duration: 10000 },
    },
    {
      id: 'upload-decision',
      type: 'decision',
      target: { nodeId: 'upload' },
      subtitle: 'Want me to use our sample dataset, or would you like to upload one of your own recordings?',
      audio: { src: `${AUDIO_BASE}/upload-decision.mp3?${AUDIO_VERSION}`, duration: 4000 },
      decision: {
        prompt: 'Want me to use our sample dataset, or would you like to upload a recording?',
        options: [
          { label: 'Use sample dataset', icon: 'mic', nextStepId: 'upload-inject-uploading' },
          { label: 'I\u2019ll upload mine', icon: 'upload', nextStepId: 'upload-interact' },
        ],
        timeoutMs: 6000,
        defaultOptionIndex: 0,
      },
    },
    {
      id: 'upload-interact',
      type: 'interact',
      target: { nodeId: 'upload' },
      subtitle: 'Go ahead and drag a file onto the upload area. I\'ll wait for you.',
      audio: { src: `${AUDIO_BASE}/upload-interact.mp3?${AUDIO_VERSION}`, duration: 3500 },
      autoAdvanceAfter: 4000,
    },

    // === INJECT: Simulate file upload progress ===
    {
      id: 'upload-inject-uploading',
      type: 'inject',
      target: { nodeId: 'upload' },
      subtitle: 'Uploading file\u2026',
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'uploading', fileName: 'demo_call.mp3', progress: 35 } },
      },
      autoAdvanceAfter: 250,
    },
    {
      id: 'upload-inject-progress',
      type: 'inject',
      target: { nodeId: 'upload' },
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'uploading', fileName: 'demo_call.mp3', progress: 72 } },
      },
      autoAdvanceAfter: 200,
    },
    {
      id: 'upload-inject-almost',
      type: 'inject',
      target: { nodeId: 'upload' },
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'uploading', fileName: 'demo_call.mp3', progress: 95 } },
      },
      autoAdvanceAfter: 150,
    },
    {
      id: 'upload-inject-done',
      type: 'inject',
      target: { nodeId: 'upload' },
      subtitle: 'Upload complete.',
      audio: { src: `${AUDIO_BASE}/upload-inject.mp3?${AUDIO_VERSION}`, duration: 1500 },
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'done', fileName: 'demo_call.mp3' } },
      },
    },

    // === STEP 2: Transcribe Audio (with live status animation) ===
    {
      id: 'transcribe-inject-processing',
      type: 'inject',
      target: { nodeId: 'auto-process' },
      delayBefore: 150,
      subtitle: 'BigSplice automatically transcribes your recordings at up to 160x real-time speed per GPU. With 500 GPUs in our cluster, we can massively parallelize large ingest jobs \u2014 transcribing a full year of call recordings in under an hour.',
      audio: { src: `${AUDIO_BASE}/transcribe-highlight.mp3?${AUDIO_VERSION}`, duration: 19000 },
      inject: {
        nodeId: 'auto-process',
        data: { demoState: { status: 'processing' } },
      },
      cursorPath: [
        { x: -60, y: 0, duration: 500 },
        { x: 0, y: 0, duration: 400 },
      ],
    },
    {
      id: 'transcribe-inject-complete',
      type: 'inject',
      target: { nodeId: 'auto-process' },
      subtitle: 'Transcription complete. The data is ready to explore.',
      audio: { src: `${AUDIO_BASE}/transcribe-wait.mp3?${AUDIO_VERSION}`, duration: 3500 },
      inject: {
        nodeId: 'auto-process',
        data: { demoState: { status: 'complete' } },
      },
    },

    // === STEP 3: View Data ===
    {
      id: 'dataset-highlight',
      type: 'highlight',
      target: { nodeId: 'sheets' },
      subtitle: 'Now let\u2019s view the data and start exploring the results.',
      audio: { src: `${AUDIO_BASE}/dataset-highlight.mp3?${AUDIO_VERSION}`, duration: 3000 },
      cursorPath: [
        { x: -40, y: -30, duration: 400 },
        { x: 0, y: 0, duration: 400 },
      ],
    },
    // Pre-inject mock data into downstream nodes BEFORE file selection triggers them
    {
      id: 'sheets-pre-inject',
      type: 'inject',
      inject: {
        nodeId: 'sheets',
        data: {
          demoState: {
            schema: DEMO_SCHEMA,
            rows: DEMO_ROWS,
            rowCount: 847,
          },
        },
      },
      autoAdvanceAfter: 100,
    },
    {
      id: 'search-pre-inject',
      type: 'inject',
      inject: {
        nodeId: 'search',
        data: {
          demoState: {},
        },
      },
      autoAdvanceAfter: 100,
    },
    // Now inject the file \u2014 downstream nodes already have demoState and won\u2019t call APIs
    {
      id: 'dataset-inject',
      type: 'inject',
      target: { nodeId: 'sheets' },
      subtitle: 'Loading data\u2026 Watch how the data flows through the pipeline.',
      audio: { src: `${AUDIO_BASE}/dataset-selected.mp3?${AUDIO_VERSION}`, duration: 5500 },
      inject: {
        nodeId: 'sheets',
        data: { selectedFile: DEMO_FILE },
      },
    },

    // === STEP 4: Browse Data (mock data already loaded via demoState) ===
    {
      id: 'sheets-highlight',
      type: 'highlight',
      target: { nodeId: 'sheets' },
      delayBefore: 400,
      subtitle: 'Here\'s your data in spreadsheet form. Each row is a transcribed call \u2014 you can see the caller, transcript, sentiment, and more.',
      audio: { src: `${AUDIO_BASE}/sheets-highlight.mp3?${AUDIO_VERSION}`, duration: 8500 },
      cursorPath: [
        { x: -100, y: -60, duration: 500 },
        { x: 0, y: 0, duration: 500 },
        { x: 50, y: 30, duration: 400 },
      ],
    },
    {
      id: 'sheets-decision',
      type: 'decision',
      target: { nodeId: 'sheets' },
      subtitle: '',
      decision: {
        prompt: 'Want to explore the data?',
        options: [
          { label: 'Explore the data', icon: 'search', nextStepId: 'sheets-interact' },
          { label: 'Show me search', icon: 'arrow-right', nextStepId: 'search-highlight' },
          { label: 'Skip to API', icon: 'skip-forward', nextStepId: 'api-highlight' },
        ],
        timeoutMs: 6000,
        defaultOptionIndex: 1,
      },
    },
    {
      id: 'sheets-interact',
      type: 'interact',
      target: { nodeId: 'sheets' },
      subtitle: 'Take a moment to browse through the data. Click any column header to sort, or use the filter controls.',
      audio: { src: `${AUDIO_BASE}/sheets-interact.mp3?${AUDIO_VERSION}`, duration: 7500 },
      autoAdvanceAfter: 4000,
    },

    // === STEP 5: Full-Text Search ===
    {
      id: 'search-highlight',
      type: 'highlight',
      target: { nodeId: 'search' },
      subtitle: 'Full-text search across all your data. Find specific phrases, patterns, or any keyword in seconds.',
      audio: { src: `${AUDIO_BASE}/search-highlight.mp3?${AUDIO_VERSION}`, duration: 7000 },
      cursorPath: [
        { x: -60, y: -40, duration: 400 },
        { x: 0, y: 0, duration: 500 },
      ],
    },
    {
      id: 'search-decision',
      type: 'decision',
      target: { nodeId: 'search' },
      subtitle: '',
      decision: {
        prompt: 'Want to try a search?',
        options: [
          { label: 'Watch demo search', icon: 'play', nextStepId: 'search-demo' },
          { label: 'Try it myself', icon: 'keyboard', nextStepId: 'search-interact' },
        ],
        timeoutMs: 5000,
        defaultOptionIndex: 0,
      },
    },
    {
      id: 'search-demo',
      type: 'interact',
      target: { nodeId: 'search' },
      subtitle: 'Let me run a quick search \u2014 watch this.',
      audio: { src: `${AUDIO_BASE}/search-typing.mp3?${AUDIO_VERSION}`, duration: 2500 },
      interaction: { action: 'type', delay: 100, value: 'customer complaint' },
      autoAdvanceAfter: 1200,
    },
    // Inject mock search results after typing
    {
      id: 'search-inject-results',
      type: 'inject',
      target: { nodeId: 'search' },
      inject: {
        nodeId: 'search',
        data: {
          demoState: {
            results: DEMO_SEARCH_RESULTS,
            resultCount: 3,
          },
        },
      },
      autoAdvanceAfter: 1200,
    },
    {
      id: 'search-interact',
      type: 'interact',
      target: { nodeId: 'search' },
      subtitle: 'Type any search term into the search box. Try phrases like "refund request" or "escalation".',
      audio: { src: `${AUDIO_BASE}/search-interact.mp3?${AUDIO_VERSION}`, duration: 6500 },
      autoAdvanceAfter: 4000,
    },

    // === STEP 6: API Access ===
    {
      id: 'api-highlight',
      type: 'highlight',
      target: { nodeId: 'api-endpoint' },
      subtitle: 'Connect your data to any tool via REST API \u2014 including Claude, GPT, Zapier, or your own applications. Generate an API key and you\'re ready to go.',
      audio: { src: `${AUDIO_BASE}/api-highlight.mp3?${AUDIO_VERSION}`, duration: 13000 },
      cursorPath: [
        { x: -60, y: -30, duration: 500 },
        { x: 0, y: 0, duration: 400 },
      ],
    },

    // === ENDING ===
    {
      id: 'ending',
      type: 'narrate',
      subtitle: 'That\'s the complete pipeline \u2014 from raw recordings to searchable transcripts with API access. Ready to build your own?',
      audio: { src: `${AUDIO_BASE}/ending.mp3?${AUDIO_VERSION}`, duration: 7500 },
    },
    {
      id: 'ending-decision',
      type: 'decision',
      subtitle: '',
      decision: {
        prompt: 'What would you like to do next?',
        options: [
          { label: 'Start my own flow', icon: 'rocket', nextStepId: '__dismiss' },
          { label: 'Replay demo', icon: 'repeat', nextStepId: '__replay' },
          { label: 'Ask a question', icon: 'help-circle', nextStepId: '__ask' },
        ],
        timeoutMs: 0,
        defaultOptionIndex: -1,
      },
    },
  ],
};
